function resourceId(resource) {
    const id = resource?.p_id ?? resource?.id
    return id === undefined || id === null || id === '' || id === 'new' ? null : String(id)
}

function comparableName(name) {
    return String(name || '').trim().toLowerCase()
}

export function duplicateNames(resources = []) {
    const names = new Map()
    for (const resource of resources) {
        const comparable = comparableName(resource?.name)
        if (!comparable) continue
        const matches = names.get(comparable) || []
        matches.push(resource.name)
        names.set(comparable, matches)
    }

    return [...names.values()]
        .filter(matches => matches.length > 1)
        .map(matches => matches[0])
}

export function hasDuplicateName(resources, name, ignoredResource = null) {
    const comparable = comparableName(name)
    if (!comparable) return false
    const ignoredId = resourceId(ignoredResource)

    return (resources || []).some(resource => {
        if (resource === ignoredResource) return false
        const id = resourceId(resource)
        if (ignoredId && id === ignoredId) return false
        return comparableName(resource?.name) === comparable
    })
}

export function nextAvailableName(resources, requestedName) {
    const baseName = String(requestedName || 'new_field').trim() || 'new_field'
    const existingNames = new Set((resources || []).map(resource => comparableName(resource?.name)))
    if (!existingNames.has(comparableName(baseName))) return baseName

    let suffix = 1
    let candidate = `${baseName}_copy`
    while (existingNames.has(comparableName(candidate))) {
        suffix += 1
        candidate = `${baseName}_copy_${suffix}`
    }
    return candidate
}

export function findFieldRenames(previousFields = [], nextFields = []) {
    const previousById = new Map()
    for (const field of previousFields) {
        const id = resourceId(field)
        if (id) previousById.set(id, field)
    }

    return nextFields.flatMap(field => {
        const id = resourceId(field)
        const previous = id ? previousById.get(id) : null
        if (!previous || previous.name === field.name) return []
        return [{ oldName: previous.name, newName: field.name }]
    })
}

export function shouldPropagateTableRename({ isNewTable = false, oldName, newName }) {
    return !isNewTable && oldName !== newName
}

export function applyIncomingFieldRenames(tables, tableName, renames = []) {
    const renameByOldName = new Map(
        renames
            .filter(rename => rename.oldName && rename.newName && rename.oldName !== rename.newName)
            .map(rename => [rename.oldName, rename.newName])
    )
    if (!renameByOldName.size) return []

    const changes = []
    for (const table of tables || []) {
        for (const field of table.fields || []) {
            if (!field.fk || field.fk_table !== tableName) continue
            const newFkField = renameByOldName.get(field.fk_field)
            if (!newFkField) continue

            changes.push({
                field,
                oldFkField: field.fk_field,
                newFkField
            })
            field.fk_field = newFkField
        }
    }
    return changes
}

export function revertIncomingFieldRenames(changes = []) {
    for (const change of changes) {
        if (change.field?.fk_field === change.newFkField) {
            change.field.fk_field = change.oldFkField
        }
    }
}

export function fieldReferencePayload(changes = []) {
    return changes.flatMap(({ field }) => {
        const id = resourceId(field)
        if (!id) return []
        return [{
            resource: 'field',
            p_id: field.p_id,
            fk_field: field.fk_field
        }]
    })
}

export function renameIncomingTableReferences(tables, oldName, newName) {
    const updatedFields = []
    const updatedLogicalTables = []

    for (const table of tables || []) {
        for (const field of table.fields || []) {
            if (!field.fk || field.fk_table !== oldName) continue
            field.fk_table = newName
            updatedFields.push({
                resource: 'field',
                p_id: field.p_id,
                fk_table: newName
            })
        }

        let logicalReferenceUpdated = false
        for (const relation of table.logical_relationships || []) {
            if (relation.fk_table !== oldName) continue
            relation.fk_table = newName
            logicalReferenceUpdated = true
        }
        if (logicalReferenceUpdated) updatedLogicalTables.push(table)
    }

    return { updatedFields, updatedLogicalTables }
}
